import type { ComponentType, ReactNode } from 'react';

import type { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import type { components, ModalId, ModalViewAction } from '@heidi/schema/ml-scribe';

import type { ObjectWorkspaceRoute } from '../../contracts/projects/chat-home-path';
import type { DesktopTargetSelectorState } from '../projects/components/DesktopTargetSelector.types';

/** The `seen_modals` record a gate reads. */
export type WorkHomeModalView = components['schemas']['ModalView'];

/** The react-pdf pair the Library thumbnail renders through. */
export interface WorkHomePdfComponents {
  Document: ComponentType<Record<string, unknown> & { children?: ReactNode }>;
  Page: ComponentType<Record<string, unknown>>;
}

/**
 * The app inputs the Work-home surfaces take, bound once from
 * `src/utils/work-home-ports-wiring.ts`.
 *
 * It is its OWN seam rather than more members on the Heidi II one because its
 * members are typed in this zone's vocabulary — the desktop-target selection
 * state and the object-workspace route both live here, and the headless seam
 * may not name a `/ui` type. Four members overlap Heidi II's and bind the same
 * app functions at the wiring.
 *
 * The wiring is imported for effect by `src/components/layout/scribe-layout.tsx`
 * rather than by the Work route: the route is a Chat file, and an import there
 * would raise the Chat ledger's `src/utils` zone count. The layout is the
 * route's own static import and is evaluated before it renders.
 */
export interface WorkHomePorts {
  // ---- Routing and composition ---------------------------------------------
  /** Opens the routine-create chat, the Work-home "create a routine" row's target. */
  useOpenRoutineCreateChat: () => () => void;
  /** The landing's desktop-target picker state, read only while it is mounted. */
  useLandingDesktopTarget: () => DesktopTargetSelectorState;

  // ---- Library documents ---------------------------------------------------
  useDocumentDownloadUrl: (documentId: string | null) => {
    data?: { url?: string } | null;
    isError: boolean;
  };
  pdf: WorkHomePdfComponents;
  /** `'pdf'` for a PDF filename; the thumbnail renders nothing otherwise. */
  getDocumentType: (filename: string) => string | undefined;

  // ---- The default-landing-page banner --------------------------------------
  useIsDefaultLandingPageSettingEnabled: () => boolean;
  useDefaultLandingPageBannerWaitMs: () => number;
  useIsClinicalRecordingActive: (includeChronicle: boolean) => boolean;
  /** The boot-landing flag as a value + setter pair over the app's atom. */
  useHasBootLandedOnHome: () => [boolean, (next: boolean) => void];
  /** The pathname the document itself was loaded at, or `null`. */
  readBootPathname: () => string | null;
  isHomeBootPath: (path: string) => boolean;
  /**
   * The user's own default-page choice: `null` when they have never chosen,
   * `undefined` while the read is pending. The value catalog stays app-side.
   */
  useQueryDefaultLandingPage: (enabled: boolean) => {
    data: string | null | undefined;
  };
  useMutateMarkModalAsViewed: () => (input: {
    modal_id: ModalId;
    increment_count: boolean;
    action?: ModalViewAction;
  }) => void;
  /** The source token is the one the settings modal already records for this banner. */
  useSettingsModal: () => {
    open(href: string, source: 'default_landing_page_banner'): void;
  };
  /** Settings → Display controls, scrolled to the Default page row. */
  settingsHref: string;
  isModalClosed: (
    modalId: ModalId,
    view: WorkHomeModalView | null | undefined
  ) => boolean;
  parseTimeStampOrNull: (time: string | null | undefined, isUtc?: boolean) => Date | null;
  trackButtonClicked: (input: {
    screen_name: ScreenName;
    action: ButtonAction;
    label: string;
  }) => void;
  trackDefaultLandingPageBannerViewed: () => void;

  // ---- The object-workspace mount decision ----------------------------------
  /** `() => useFeatureFlagEnabled(KEY)`, so the package never sees a key. */
  useAgentHomeNavFlag: () => boolean | undefined;
  useObjectWorkspaceFlag: () => boolean | undefined;
  usePosthogFlagsReady: () => boolean;
  useIsProjectsEnabled: () => boolean;
}

export type { ObjectWorkspaceRoute };

let ports: WorkHomePorts | null = null;

export const configureWorkHomePorts = (next: WorkHomePorts): void => {
  ports = next;
};

export const getWorkHomePorts = (): WorkHomePorts => {
  if (!ports) {
    throw new Error(
      '[agents/work-home-ports] ports not configured — the app must load ' +
        "'@/utils/work-home-ports-wiring' at bootstrap; tests must call " +
        'configureWorkHomePorts() with a double first.'
    );
  }
  return ports;
};

export const resetWorkHomePortsForTests = (): void => {
  ports = null;
};
